import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box, Divider } from '@material-ui/core';
import {
  SPRAY_TYPES, SPRAY_TYPE_LABELS, SPRAY_TYPE_COLORS, getSprayTypeForArea,
  SERVICES, PLANT_BEARING_TYPES, resolveAreaType, isExclusionArea,
} from '../../../SiteTakeoff/siteTakeoffConstants';
import LaborEstimate from '../../../SiteTakeoff/LaborEstimate';

const useStyles = makeStyles(() => ({
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginBottom: 20,
  },
  statCard: {
    padding: 14,
    background: '#f5f5f5',
    borderRadius: 8,
  },
  statLabel: {
    fontSize: 11,
    color: '#999',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  statValue: {
    fontSize: 18,
    fontWeight: 500,
    color: '#333',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  sprayBox: {
    padding: 14,
    background: '#ECFDF5',
    borderRadius: 8,
    borderLeft: '3px solid #10B981',
    marginBottom: 20,
  },
  sprayTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#047857',
    marginBottom: 8,
  },
  sprayRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: 13,
  },
  sprayDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  empty: {
    padding: 32,
    textAlign: 'center',
    color: '#999',
    fontSize: 13,
  },
}));

const formatSF = (n) => (parseFloat(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const TakeoffMode = ({ areas = [], rateOverrides = {} }) => {
  const classes = useStyles();

  const summary = useMemo(() => {
    // Filter out exclusion areas from summary
    const parentAreas = areas.filter(a => !isExclusionArea(a));
    if (!parentAreas.length) return null;

    // Build exclusion SF map for net area calculation
    const exclusionSFByParent = {};
    areas.forEach(a => {
      if (!isExclusionArea(a)) return;
      const pid = typeof a.parentAreaId === 'object' ? a.parentAreaId.toString() : a.parentAreaId;
      exclusionSFByParent[pid] = (exclusionSFByParent[pid] || 0) + (parseFloat(a.area) || 0);
    });

    let totalSF = 0;
    let totalLF = 0;
    let totalTrees = 0;
    let modifierCount = 0;
    const sprayCoverage = {
      [SPRAY_TYPES.TURF]: 0,
      [SPRAY_TYPES.ORNAMENTAL]: 0,
      [SPRAY_TYPES.BARE_GROUND]: 0,
    };
    let totalSpraySF = 0;
    let areasWithSpray = 0;

    parentAreas.forEach(a => {
      const areaId = a._id || a.id;
      const excSF = exclusionSFByParent[areaId] || 0;
      const sf = (parseFloat(a.area) || 0) - excSF;
      const lf = a.geometryType === 'polyline'
        ? (parseFloat(a.length) || 0)
        : (parseFloat(a.perimeter) || 0);
      totalSF += sf;
      totalLF += lf;

      if (a.plantDensity?.treeCount) totalTrees += a.plantDensity.treeCount;

      // Count areas that have non-default modifiers
      const m = a.modifiers;
      if (m && (
        m.access === 'obstructed' || m.slope === 'sloped' ||
        m.litterExposure === 'high' || m.weedPressure === 'med' ||
        m.weedPressure === 'high' || m.limitedAccess || m.blowRestrictions
      )) {
        modifierCount++;
      }

      // Spray coverage
      if (a.geometryType !== 'point' && sf > 0) {
        const hasPreEm = (a.servicesRequired || []).includes(SERVICES.PRE_EMERGENT);
        if (hasPreEm) {
          const sprayType = getSprayTypeForArea(a.areaType);
          if (sprayType !== SPRAY_TYPES.NONE) {
            sprayCoverage[sprayType] = (sprayCoverage[sprayType] || 0) + sf;
            totalSpraySF += sf;
            areasWithSpray++;
          }
        }
      }
    });

    return {
      totalSF,
      totalLF,
      totalAcres: totalSF / 43560,
      totalTrees,
      modifierCount,
      sprayCoverage,
      totalSpraySF,
      areasWithSpray,
    };
  }, [areas]);

  if (!summary) {
    return (
      <div className={classes.empty}>
        No areas yet. Draw some areas to see the takeoff summary.
      </div>
    );
  }

  return (
    <Box>
      {/* Quick stats grid */}
      <div className={classes.statsGrid}>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Property</div>
          <div className={classes.statValue}>{formatSF(summary.totalSF)} SF</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Perimeter</div>
          <div className={classes.statValue}>{formatSF(summary.totalLF)} LF</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Acres</div>
          <div className={classes.statValue}>{summary.totalAcres.toFixed(2)}</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>Trees</div>
          <div className={classes.statValue}>{summary.totalTrees}</div>
        </div>
      </div>

      {/* Pre-emergent coverage */}
      {summary.totalSpraySF > 0 && (
        <div className={classes.sprayBox}>
          <div className={classes.sprayTitle}>Pre-emergent coverage</div>
          {[SPRAY_TYPES.TURF, SPRAY_TYPES.ORNAMENTAL, SPRAY_TYPES.BARE_GROUND].map(st => {
            const sf = summary.sprayCoverage[st] || 0;
            if (sf <= 0) return null;
            return (
              <div key={st} className={classes.sprayRow}>
                <span>
                  <span className={classes.sprayDot} style={{ background: SPRAY_TYPE_COLORS[st] }} />
                  {SPRAY_TYPE_LABELS[st]}
                </span>
                <strong>{formatSF(sf)} SF</strong>
              </div>
            );
          })}
          <div className={classes.sprayRow} style={{ borderTop: '1px solid #A7F3D0', marginTop: 6, paddingTop: 6 }}>
            <span style={{ fontWeight: 600 }}>Total spray</span>
            <strong>{formatSF(summary.totalSpraySF)} SF</strong>
          </div>
          <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>
            {summary.areasWithSpray} {summary.areasWithSpray === 1 ? 'area' : 'areas'}
          </div>
        </div>
      )}

      <Divider style={{ margin: '16px 0 8px' }} />

      {/* Labor estimate breakdown — reuses V1 LaborEstimate component */}
      <LaborEstimate areas={areas} rateOverrides={rateOverrides} />
    </Box>
  );
};

export default TakeoffMode;
